import React from 'react';
import checkObjectTree from '../../utils/checkObjectTree';

export const sharingMappings = {
	stella: 'Stella',
	STADTMOBIL: 'Stadtmobil',
	FLINKSTER: 'Flinkster',
	car2go: 'ShareNow',
	CALL_A_BIKE: 'RegioRad',
	CallABike: 'RegioRad',
};

const carClasses = {
	CAR: 'Auto',
	SMALLFAMILYCAR: 'Kleines Familienauto',
	CITYCAR: 'Stadtauto',
	MULTIPURPOSECARS: 'Mehrzweckfahrzeug',
	LARGEFAMILYCAR: 'Großes Familienauto',
	SUPERMINI: 'Kleinwagen',
	LUXURYCAR: 'Luxus',
	MOTORCYCLE: 'Motorrad / Motorroller',
	SCOOTER: 'Motorroller',
	BIKE: 'Fahrrad',
	MINI: {
		ESCOOTER: 'E-Scooter',
	},
};

function buildAddress(addressString) {
	if (!addressString) return [''];
	const addressData = addressString.split(', ').filter((data) => data.trim());
	return addressData;
}

function getUsedClasses(vehicles) {
	const usedClasses = [];
	if (Array.isArray(vehicles)) {
		vehicles.forEach((vehicle) => {
			if (usedClasses.indexOf(carClasses[vehicle.carClass ?? vehicle.vehType]) < 0) {
				usedClasses.push(carClasses[vehicle.carClass ?? vehicle.vehType]);
			}
		});
	} else {
		usedClasses.push(carClasses[vehicles.carClass ?? vehicles.vehType]);
	}
	return usedClasses;
}

/** Renders vehicle class names as stacked <span> elements; returns '-' when none are present. */
function buildVehicleTypeSpans(usedClasses) {
	if (usedClasses.length === 0) return '-';
	return usedClasses.map((clazz, i) => {
		if (i === usedClasses.length - 1) {
			return <span key={`clazz-${clazz}`}>{clazz}</span>;
		}
		return (
			<span key={`clazz-${clazz}-br`}>
				{clazz}
				<br />
			</span>
		);
	});
}

/** Assembles the common sharing-item shape, deriving type/icon from sharingMappings; used by all buildXxx functions. */
function createSharingItem(item, fields) {
	const icon = (sharingMappings[item.type] || item.type)?.toLowerCase();
	return {
		id: item.id,
		linkname: fields.linkname,
		type: 'sharing',
		icon,
		...fields,
	};
}

export function buildStadtmobilFlinksterItem(item, operators, linkname = 'Buchen') {
	let usedClasses = [];
	let freeVehicles = 0;
	let firstVehicleDetails = false;
	const address = buildAddress(item.coordInfoDetails.station.addr);

	if (checkObjectTree(item, ['coordInfoDetails', 'station', 'vehDetails'])) {
		usedClasses = getUsedClasses(item.coordInfoDetails.station.vehDetails);

		if (Array.isArray(item.coordInfoDetails.station.vehDetails)) {
			freeVehicles = item.coordInfoDetails.station.vehDetails.length;
			[firstVehicleDetails] = item.coordInfoDetails.station.vehDetails;
		} else {
			freeVehicles = 1;
			firstVehicleDetails = item.coordInfoDetails.station.vehDetails;
		}
	}

	const type = sharingMappings[item.type] || item.type;
	const operator = operators[type][0];
	return createSharingItem(item, {
		title: item.desc,
		availableVehicleCount: freeVehicles,
		subtitle: '',
		address,
		vehicletypesLength: usedClasses.length,
		vehicletypes: buildVehicleTypeSpans(usedClasses),
		iosDeeplink: firstVehicleDetails && firstVehicleDetails.dLIOS,
		iosStore: operator?.ios,
		androidDeeplink: firstVehicleDetails && firstVehicleDetails.dLAndroid,
		androidStore: operator?.android,
		windowsDeeplink: firstVehicleDetails && firstVehicleDetails.dLWindows,
		windowsStore: operator?.windows,
		uri: operator?.URL,
		linkname,
	});
}

export function buildStellaItem(item, operator, linkname = 'Buchen') {
	const address = buildAddress(item.coordInfoDetails.addr);

	let carType = carClasses[item.coordInfoDetails.vehType];
	if (item.coordInfoDetails.vehType === 'MOTORCYCLE') {
		carType = 'E-Roller';
	}

	return createSharingItem(item, {
		title: item.desc,
		availableVehicleCount: 1,
		subtitle: item.coordInfoDetails.fuelLevel
			? `Ladezustand ${item.coordInfoDetails.fuelLevel} %`
			: 'Ladezustand unbekannt',
		address,
		vehicletypes: carType,
		// iosDeeplink: sth.dLIOS,
		iosStore: operator?.ios,
		// androidDeeplink: sth.dLAndroid,
		androidStore: operator?.android,
		// windowsDeeplink: stg.dLWindows,
		windowsStore: operator?.windows,
		uri: operator?.URL,
		linkname,
	});
}

export function buildEScooterItem(item, operator, linkname = 'Buchen') {
	const address = buildAddress(item.coordInfoDetails.addr);
	const vehicletype =
		item.coordInfoDetails.vehType === 'MINI'
			? carClasses.MINI[item.coordInfoDetails.miniVehicleClass]
			: carClasses[item.coordInfoDetails.vehType];

	return createSharingItem(item, {
		title: vehicletype,
		availableVehicleCount: 1,
		subtitle: `Reichweite: ${item.coordInfoDetails.rangeKm} km`,
		address,
		vehicletypes: vehicletype,
		iosDeeplink: item.coordInfoDetails.dLIOS,
		iosStore: operator?.ios,
		androidDeeplink: item.coordInfoDetails.dLAndroid,
		androidStore: operator?.android,
		windowsDeeplink: item.coordInfoDetails.dLWindows,
		windowsStore: operator?.windows,
		uri: operator?.URL,
		linkname,
	});
}

export function buildRegiorad(item, operator, linkname = 'Buchen') {
	const address = buildAddress(item.coordInfoDetails.addr);
	const vehicletypes = item.coordInfoDetails.station.vehicleTypes?.map(
		(vehType) => carClasses[vehType],
	);

	return createSharingItem(item, {
		title: item.desc,
		availableVehicleCount: item.coordInfoDetails.station.bikeSlots.freeVehicles,
		subtitle: '',
		address,
		vehicletypes,
		iosDeeplink: item.coordInfoDetails.station.dLIOS,
		iosStore: operator?.ios,
		androidDeeplink: item.coordInfoDetails.station.dLAndroid,
		androidStore: operator?.android,
		windowsDeeplink: item.coordInfoDetails.station.dLWindows,
		windowsStore: operator?.windows,
		uri: operator?.URL,
		linkname,
	});
}
